import Logger from '@cvent/nucleus-logging';
import { hideLoadingOnError } from 'nucleus-guestside-site/src/redux/modules/dialogContainer';
import {
  getAdmissionItems,
  getEventRegistrations,
  getPrimaryRegistrationId,
  getRegistrationPathId,
  getRegistrationTypeId
} from './selectors';
import { getAuthenticationByConfirmation, createInitialRegCart } from './internal';
import { calculatePricing, getRegCartPricingInfo, getRegCartPricingInfoForCheckout } from './pricing';
import { restoreTravelCartIntoState, saveTravelRegistration } from '../../travelCart';
import { travelCartNotFound } from '../../travelCart/index';
import {
  CREATE_REG_CART_PENDING, CREATE_REG_CART_FAILURE, CREATE_REG_CART_SUCCESS,
  START_MODIFICATION_PENDING, START_MODIFICATION_FAILURE, START_MODIFICATION_SUCCESS,
  FINALIZE_CHECKOUT_PENDING, FINALIZE_CHECKOUT_FAILURE, FINALIZE_CHECKOUT_SUCCESS,
  INITIATE_CANCEL_REGISTRATION_PENDING, INITIATE_CANCEL_REGISTRATION_SUCCESS,
  START_CANCEL_REGISTRATION_CHECKOUT_PENDING, START_CANCEL_REGISTRATION_CHECKOUT_FAILURE,
  START_CANCEL_REGISTRATION_CHECKOUT_SUCCESS,
  FINALIZE_CANCEL_REGISTRATION_PENDING, FINALIZE_CANCEL_REGISTRATION_SUCCESS,
  INITIATE_DECLINE_REGISTRATION_SUCCESS,
  INITIATE_WAITLIST_REGISTRATION_FAILURE,
  INITIATE_WAITLIST_REGISTRATION_SUCCESS,
  START_WAITLIST_REGISTRATION_CHECKOUT_FAILURE,
  FINALIZE_DECLINE_REGISTRATION_FAILURE, FINALIZE_DECLINE_REGISTRATION_SUCCESS,
  SET_CURRENT_EVENT_REGISTRATION_ID, UPDATE_REG_CART_PENDING, UPDATE_REG_CART_SUCCESS, UPDATE_REG_CART_FAILURE
} from './actionTypes';
import { filterEventSnapshot } from '../../actions';
import { loadAvailableCapacityCounts } from '../../capacity';
import { setQueryParamsInUserSession } from '../../userSession';
import { populateVisibleProducts, populateRegCartVisibleProducts, resetVisibleProducts } from '../../visibleProducts';
import { getModErrors, findKnownErrorResourceKey } from '../errors';
import { openKnownErrorDialog } from '../../../dialogs';
import { hasAnyBookingsToProcess } from '../../../utils/travelUtils';
import { getTravelRegForm } from '../../travelCart/selectors';
import { getEventRegistrationId } from '../../selectors/currentRegistrant';
import { push, setIn } from 'icepick';
import { getCurrentPageId } from '../../pathInfo';
import { isGuestProductSelectionEnabledOnRegPath } from '../../selectors/currentRegistrationPath';
import { getDefaultAdmissionItemIdSelectionForRegType } from '../../selectors/event';
import { updateGuestsToMatchPrimaryReg } from './guests';
import { size } from 'lodash';

const LOG = new Logger('redux/registrationForm/regCart/workflow');

/**
 * Creates a RegCart using inviteeId or contactId if provided, if not an empty RegCart
 */
export function startRegistration(eventId, inviteeId, contactId, defaultRegTypeId) {
  return async (dispatch, getState) => {
    await setQueryParamsInUserSession(dispatch, getState());
    const { accessToken, clients: { regCartClient },
      userSession: { isTestMode, referenceId, defaultRegPathId, defaultRegPackId, httpReferrer } } = getState();
    dispatch({
      type: CREATE_REG_CART_PENDING
    });
    let response;

    try {
      response = await createInitialRegCart(regCartClient, accessToken, getState(), inviteeId,
        contactId, eventId, isTestMode, referenceId, defaultRegTypeId, defaultRegPathId, defaultRegPackId,
        httpReferrer);
      const currentEventRegistrationId = getPrimaryRegistrationId(response.regCart);
      const { registrationPathId, registrationTypeId } = Object.values(response.regCart.eventRegistrations)[0];
      if (defaultRegTypeId !== registrationTypeId || defaultRegPathId !== registrationPathId) {
        await dispatch(filterEventSnapshot(response.regCart.eventSnapshotVersions[eventId],
          registrationTypeId, registrationPathId));
        await dispatch(loadAvailableCapacityCounts());
      }
      dispatch({
        type: CREATE_REG_CART_SUCCESS,
        payload: { regCart: response.regCart, validationMessages: response.validationMessages,
          currentEventRegistrationId }
      });
      LOG.debug('createRegCart success');
    } catch (error) {
      LOG.info('createRegCart failed', error);
      dispatch({ type: CREATE_REG_CART_FAILURE, payload: { error } });
      dispatch(hideLoadingOnError());
      throw error;
    }
    await dispatch(calculatePricing());
    await dispatch(resetVisibleProducts());
    await dispatch(populateVisibleProducts());
    return response;
  };
}

export function startModification() {
  return async (dispatch, getState) => {
    const {
      event,
      registrationForm,
      accessToken,
      clients: { regCartClient },
      userSession: { isPlanner }
    } = getState();

    let response;
    try {
      const authToken = isPlanner ? accessToken :
        await getAuthenticationByConfirmation(event, registrationForm, accessToken, regCartClient);
      LOG.debug('startModification start');
      dispatch({ type: START_MODIFICATION_PENDING });
      response = await regCartClient.createRegModCart(authToken);
      const { regCart } = response;
      const currentEventRegistrationId = getPrimaryRegistrationId(regCart);
      dispatch({ type: START_MODIFICATION_SUCCESS, payload: { regCart } });
      dispatch({ type: SET_CURRENT_EVENT_REGISTRATION_ID, payload: { currentEventRegistrationId } });
      if (response.regCart.hasTravel) {
        dispatch(restoreTravelCartIntoState(response.regCart.regCartId));
      } else {
        await dispatch(travelCartNotFound(response.regCart.regCartId));
      }
      const { registrationTypeId, registrationPathId } = regCart.eventRegistrations[currentEventRegistrationId];
      await dispatch(filterEventSnapshot(regCart.eventSnapshotVersions[event.id],
        registrationTypeId, registrationPathId));
    } catch (error) {
      await dispatch({ type: START_MODIFICATION_FAILURE, payload: { error } });
      if (getModErrors.isKnownError(error)) {
        await dispatch(openKnownErrorDialog(findKnownErrorResourceKey(error.responseBody.validationMessages)));
        return;
      }
      dispatch(hideLoadingOnError());
      throw error;
    }
    await dispatch(calculatePricing());
    await dispatch(populateRegCartVisibleProducts());
  };
}

const checkoutCompletionDelay = 2000;
/**
 * Add a delay between receiving indication from the server that checkout has completed and reacting to it so that
 * the user has a chance to see checkout progress has hit 100%
 */
function delayCheckoutCompletion() {
  return new Promise((resolve) => {
    setTimeout(() => resolve(), checkoutCompletionDelay);
  });
}

export function finalizeRegistration() {
  return async (dispatch, getState) => {
    const { accessToken, registrationForm: { regCartPayment, regCart: { regCartId } },
      regCartPricing, event, clients: { regCartClient },
      testSettings: { registrationCheckoutTimeout } } = getState();
    const feesEnabled = event.eventFeatureSetup.fees.fees;
    let paymentAmount = regCartPricing.netFeeAmountChargeWithPaymentAmountServiceFee;
    if (regCartPayment.selectedPaymentMethod === 'noPayment') {
      paymentAmount = 0;
    }
    const pricingInfo = {
      ...getRegCartPricingInfo(regCartPayment, feesEnabled),
      paymentAmount
    };
    LOG.debug('finalizeRegistration', pricingInfo);
    dispatch({ type: FINALIZE_CHECKOUT_PENDING, payload: { checkoutProgress: 0 } });
    try {
      await regCartClient.calculateRegCartPricing(accessToken, regCartId, pricingInfo);
      await regCartClient.startRegCartCheckout(
        accessToken, regCartId, getRegCartPricingInfoForCheckout(pricingInfo), registrationCheckoutTimeout);
      return await getFinalState(regCartClient, accessToken, regCartId, dispatch);
    } catch (error) {
      dispatch({ type: FINALIZE_CHECKOUT_FAILURE, payload: { error } });
      dispatch(hideLoadingOnError());
      throw error;
    }
  };
}

async function getFinalState(regCartClient, accessToken, regCartId, dispatch) {
  const finalStatus = await regCartClient.waitForRegCartCheckoutCompletion(
    accessToken, regCartId, regCartStatus => {
      const checkoutProgress = regCartStatus.progress || 0;
      dispatch({ type: FINALIZE_CHECKOUT_PENDING, payload: { checkoutProgress } });
      LOG.debug('received reg cart progress', regCartStatus);
    });
  dispatch({ type: FINALIZE_CHECKOUT_PENDING, payload: { checkoutProgress: 100 } });
  await delayCheckoutCompletion();
  const regCartResponse = await regCartClient.getRegCart(accessToken, regCartId);
  dispatch({ type: FINALIZE_CHECKOUT_SUCCESS, payload: { regCart: regCartResponse } });
  LOG.debug('finished waiting for reg cart checkout', finalStatus);
  return finalStatus;
}

export function waitForRegCartCheckoutCompletionUi(): ThunkedAction<Promise<void>> {
  return async (dispatch: Dispatch<*>, getState: GetState) => {
    try {
      const { accessToken, registrationForm: { regCart: { regCartId } }, clients: { regCartClient } } = getState();
      dispatch({ type: FINALIZE_CHECKOUT_PENDING, payload: { checkoutProgress: 0 } });
      return await getFinalState(regCartClient, accessToken, regCartId, dispatch);
    } catch (error) {
      dispatch({ type: FINALIZE_CHECKOUT_FAILURE, payload: { error } });
      dispatch(hideLoadingOnError());
      throw error;
    }
  };
}

export function startCancelRegistration() {
  return async (dispatch, getState) => {
    const { event, registrationForm, accessToken, clients: { regCartClient }, userSession: { isPlanner } } = getState();
    let response;
    try {
      LOG.debug('startCancelRegistration start');
      dispatch({ type: INITIATE_CANCEL_REGISTRATION_PENDING });
      const authToken = isPlanner ? accessToken :
        await getAuthenticationByConfirmation(event, registrationForm, accessToken, regCartClient);
      LOG.debug('startCancellation start');
      response = await regCartClient.createCancelRegistrationCart(authToken);
      const regCart = response.regCart;
      const currentEventRegistrationId = getPrimaryRegistrationId(regCart);
      dispatch({ type: SET_CURRENT_EVENT_REGISTRATION_ID, payload: { currentEventRegistrationId } });
      dispatch({ type: INITIATE_CANCEL_REGISTRATION_SUCCESS, payload: { regCart } });
      const { registrationTypeId, registrationPathId } = regCart.eventRegistrations[currentEventRegistrationId];
      await dispatch(filterEventSnapshot(regCart.eventSnapshotVersions[event.id],
        registrationTypeId, registrationPathId));
    } catch (error) {
      dispatch(hideLoadingOnError());
      throw error;
    }
  };
}

export function finalizeCancelRegistration() {
  return async (dispatch, getState) => {
    const { accessToken, registrationForm: { regCart: { regCartId } },
      clients: { regCartClient }, testSettings: { registrationCheckoutTimeout } } = getState();
    const pricingInfo = { paymentType: 'Offline' };
    LOG.debug('finalize cancel registration', pricingInfo);
    dispatch({ type: START_CANCEL_REGISTRATION_CHECKOUT_PENDING });
    try {
      await regCartClient.calculateRegCartPricing(accessToken, regCartId, pricingInfo);
      await regCartClient.startRegCartCheckout(accessToken, regCartId, pricingInfo, registrationCheckoutTimeout);
      dispatch({ type: START_CANCEL_REGISTRATION_CHECKOUT_SUCCESS });
    } catch (error) {
      dispatch(hideLoadingOnError());
      dispatch({ type: START_CANCEL_REGISTRATION_CHECKOUT_FAILURE, payload: { error } });
      throw error;
    }
    try {
      dispatch({ type: FINALIZE_CANCEL_REGISTRATION_PENDING });
      const finalStatus = await regCartClient.waitForRegCartCheckoutCompletion(accessToken, regCartId, () => {});
      await delayCheckoutCompletion();
      const regCartResponse = await regCartClient.getRegCart(accessToken, regCartId);
      dispatch({ type: FINALIZE_CANCEL_REGISTRATION_SUCCESS, payload: { regCart: regCartResponse } });
      LOG.debug('finished waiting for cancel reg cart checkout', finalStatus);
    } catch (error) {
      dispatch(hideLoadingOnError());
      throw error;
    }
  };
}

export function startDeclineRegistration(inviteeId) {
  return async (dispatch, getState) => {
    const { accessToken, clients: { regCartClient }, userSession: { eventId }, plannerRegSettings } = getState();
    LOG.debug('Decline Registration start', { eventId, inviteeId });
    const sendEmail = plannerRegSettings ? plannerRegSettings.sendEmail : true;
    try {
      const response = await regCartClient.createDeclineRegistrationCart(accessToken, eventId, inviteeId, sendEmail);
      dispatch({ type: INITIATE_DECLINE_REGISTRATION_SUCCESS, payload: { regCart: response.regCart } });
    } catch (error) {
      dispatch(hideLoadingOnError());
      throw error;
    }
  };
}
export function startWaitlistRegistration(inviteeId) {
  return async (dispatch, getState) => {
    const { accessToken, clients: { regCartClient }, userSession: { eventId } } = getState();
    LOG.debug('Waitlist Registration start', { eventId, inviteeId });
    try {
      const response = await regCartClient.createWaitlistRegistrationCart(accessToken, eventId, inviteeId);
      dispatch({ type: INITIATE_WAITLIST_REGISTRATION_SUCCESS, payload: { regCart: response.regCart } });
    } catch (error) {
      dispatch(hideLoadingOnError());
      dispatch({ type: INITIATE_WAITLIST_REGISTRATION_FAILURE, payload: { error } });
      throw error;
    }
  };
}
export function finalizeWaitlistRegistration() {
  return async (dispatch, getState) => {
    const { accessToken, registrationForm: { regCart: { regCartId } }, registrationForm: { regCart },
      clients: { regCartClient }, testSettings: { registrationCheckoutTimeout } } = getState();
    const pricingInfo = { paymentType: 'Offline' };
    LOG.debug('finalize waitlist registration', pricingInfo);
    try {
      await regCartClient.updateRegCart(accessToken, regCart);
      await regCartClient.startRegCartCheckout(accessToken, regCartId, pricingInfo, registrationCheckoutTimeout);
    } catch (error) {
      dispatch(hideLoadingOnError());
      dispatch({ type: START_WAITLIST_REGISTRATION_CHECKOUT_FAILURE, payload: { error } });
      throw error;
    }
  };
}

export function finalizeDeclineRegistration() {
  return async (dispatch, getState) => {
    const { accessToken, registrationForm: { regCart: { regCartId } }, registrationForm: { regCart },
      clients: { regCartClient }, testSettings: { registrationCheckoutTimeout } } = getState();
    const pricingInfo = { paymentType: 'Offline' };
    LOG.debug('finalize decline registration', pricingInfo);
    try {
      await regCartClient.updateRegCart(accessToken, regCart);
      await regCartClient.startRegCartCheckout(accessToken, regCartId, pricingInfo, registrationCheckoutTimeout);
    } catch (error) {
      dispatch(hideLoadingOnError());
      throw error;
    }
    try {
      const finalStatus = await regCartClient.waitForRegCartCheckoutCompletion(accessToken, regCartId, () => {});
      await delayCheckoutCompletion();
      const regCartResponse = await regCartClient.getRegCart(accessToken, regCartId);
      await dispatch({ type: FINALIZE_DECLINE_REGISTRATION_SUCCESS, payload: { regCart: regCartResponse } });
      LOG.debug('finished waiting for decline reg cart checkout', finalStatus);
    } catch (error) {
      dispatch(hideLoadingOnError());
      dispatch({ type: FINALIZE_DECLINE_REGISTRATION_FAILURE, payload: { error } });
      throw error;
    }
  };
}

export function saveRegistration(linkedInviteeIdToRemove, checkTravel, isForward) {
  return async (dispatch, getState) => {
    const { accessToken, registrationForm: { validationMessages }, regCartStatus: { lastSavedRegCart },
      clients: { regCartClient }, eventTravel } = getState();

    let { registrationForm: { regCart: cart } } = getState();
    if (checkTravel) {
      cart = { ...cart, hasTravel: !!hasAnyBookingsToProcess(getTravelRegForm(getState()), eventTravel) };
    }
    if (cart === lastSavedRegCart) {
      return { regCart: cart, validationMessages };
    }
    let response;
    dispatch({ type: UPDATE_REG_CART_PENDING });
    try {
      LOG.debug('updateRegCart', cart);
      response = await updateRegCart(getState(), regCartClient, accessToken, cart, linkedInviteeIdToRemove, isForward);
      const currentRegistrantEventRegId = getEventRegistrationId(getState());
      const regPathId = getRegistrationPathId(response.regCart, currentRegistrantEventRegId);
      const registrationTypeId = getRegistrationTypeId(response.regCart, currentRegistrantEventRegId);
      const regPath = getState().appData.registrationSettings.registrationPaths[regPathId];
      if (!regPath) {
        await dispatch(filterEventSnapshot(getState().eventSnapshotVersion, registrationTypeId, regPathId));
      }
      dispatch({
        type: UPDATE_REG_CART_SUCCESS, payload: {
          regCart: response.regCart,
          validationMessages: response.validationMessages
        }
      });
      LOG.debug('updateRegCart success');
    } catch (error) {
      LOG.info('updateRegCart failed', error);
      dispatch({ type: UPDATE_REG_CART_FAILURE, payload: { error } });
      dispatch(hideLoadingOnError());
      throw error;
    }
    await dispatch(saveTravelRegistration(isForward));
    await dispatch(calculatePricing());
    return response;
  };
}


/**
 * Updates the regCart and additionally adds admission items to each event registration under the following conditions.
 * 1. The event registration has no admission item.
 * 2. The event only has a single admission item available and the event registration has a registration type which
 *    the admission item is available for.
 *
 * It became necessary in PROD-41864 to not automatically add an admission item until the registration type allowed it
 * to prevent hard errors.
 *
 * isForward indicates whether the invitee is moving forward in the registration (next page or submit)
 */
export async function updateRegCart(state, regCartClient, accessToken, regCart, linkedInviteeIdToRemove,
  isForward = null) {
  const regCartWithPage = isForward ? setIn(regCart, ['lastSavedPageId'], getCurrentPageId(state)) : regCart;
  let response = await regCartClient.updateRegCart(accessToken, regCartWithPage, linkedInviteeIdToRemove);
  const eventRegistrationIds = Object.keys(getEventRegistrations(response.regCart));
  const isGuestProductSelectionEnabled = isGuestProductSelectionEnabledOnRegPath(state);
  // Looks at each event registration and tries to add an admission item if possible.
  for (let index = 0; index < eventRegistrationIds.length; index++) {
    const eventRegistrationId = eventRegistrationIds[index];
    const eventRegistration = response.regCart.eventRegistrations[eventRegistrationId];
    const hasNoAdmissionItem = size(getAdmissionItems(response.regCart, eventRegistrationId)) === 0;
    const isGuest = eventRegistration && eventRegistration.attendeeType === 'GUEST';
    // if the attendee is a guest and guestProductSelectionEnabled then proceed
    const updateAdmissionItem = isGuest && isGuestProductSelectionEnabled;
    if (hasNoAdmissionItem && updateAdmissionItem) {
      const defaultAdmissionItemId = getDefaultAdmissionItemIdSelectionForRegType(state,
        eventRegistration.registrationTypeId);
      if (defaultAdmissionItemId) {
        response = await regCartClient.updateRegCart(accessToken,
          setIn(response.regCart, ['eventRegistrations', eventRegistrationId, 'productRegistrations'],
            push(eventRegistration.productRegistrations, {
              productId: defaultAdmissionItemId,
              productType: 'AdmissionItem',
              quantity: 1,
              requestedAction: 'REGISTER'
            })),
          linkedInviteeIdToRemove);
      }
    }
    // if guestProductSelection not enabled match the currentRegistrants guest to primary's products
    if (hasNoAdmissionItem && isGuest && !isGuestProductSelectionEnabled) {
      const primaryEventRegId = eventRegistration.primaryRegistrationId;
      const updatedRegCart = updateGuestsToMatchPrimaryReg(response.regCart, primaryEventRegId, state);
      response = await regCartClient.updateRegCart(accessToken, updatedRegCart, linkedInviteeIdToRemove);
    }
  }
  return response;
}

/**
 * Updates the travel flag in reg cart as passed
 */
export function updateTravelFlagInRegCart(hasTravelFlag) {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      accessToken, registrationForm: { regCart }, regCartStatus: { lastSavedRegCart }, clients: { regCartClient }
    } = state;
    let response;
    dispatch({ type: UPDATE_REG_CART_PENDING });
    try {
      // need to just update the travel flag in the cart
      // so use the last saved cart, to avoid any other validations from latest local state
      response = await updateRegCart(state, regCartClient, accessToken, {
        ...lastSavedRegCart,
        hasTravel: hasTravelFlag
      });
      const savedRegCart = response.regCart;

      // and we also need to update the local state, to keep the travel flag in sync between saved and unsaved/latest
      const regCartWithUnsavedChanges = {
        ...regCart,
        hasTravel: savedRegCart.hasTravel
      };

      dispatch({
        type: UPDATE_REG_CART_SUCCESS, payload: {
          regCart: regCartWithUnsavedChanges,
          savedRegCart,
          validationMessages: response.validationMessages
        }
      });
      LOG.debug(`updateRegCart with id ${regCart.regCartId} was successful`);
    } catch (error) {
      LOG.error(`updateRegCart with id ${regCart.regCartId} failed`, error);
      dispatch({ type: UPDATE_REG_CART_FAILURE, payload: { error } });
      dispatch(hideLoadingOnError());
      throw error;
    }
  };
}
